import { useState, useEffect, useMemo } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { 
  Plus, 
  Search, 
  Filter,
  RefreshCw,
  ExternalLink,
  Target,
  ListFilter,
  FileText,
  ShieldCheck,
  ShieldAlert,
  ShieldX,
  ShieldQuestion,
  Loader2,
  Upload,
  Download,
  FileUp,
  FileDown,
  CheckCircle,
  AlertCircle,
  Trash2,
  Checkbox,
  ArrowUpDown,
  ArrowUp,
  ArrowDown
} from "lucide-react";
import LeadDetailSheet from "@/components/LeadDetailSheet";

const SOURCES = [
  { value: "sam_gov", label: "SAM.gov" },
  { value: "cleared_jobs", label: "ClearedJobs" },
  { value: "linkedin", label: "LinkedIn" },
  { value: "indeed", label: "Indeed" },
  { value: "dice", label: "Dice" },
  { value: "manual", label: "Manual" },
];

const STATUSES = [
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "call_set", label: "Call Set" },
  { value: "submitted", label: "Submitted" },
  { value: "interview", label: "Interview" },
  { value: "offer", label: "Offer" },
  { value: "rejected", label: "Rejected" },
  { value: "archived", label: "Archived" },
];

export default function Leads() {
  const { token } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verificationStats, setVerificationStats] = useState(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [sortField, setSortField] = useState("score");
  const [sortDirection, setSortDirection] = useState("desc");
  const [newLead, setNewLead] = useState({
    title: "",
    client: "",
    rate: "",
    location: "",
    clearance: "",
    tech_stack: "",
    link: "",
    source: "manual",
    notes: ""
  });

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (sourceFilter !== "all") params.append("source", sourceFilter);
      
      const response = await api.get(`/leads?${params.toString()}`);
      setLeads(response.data);
      setSelectedLeadIds([]); // Clear selection after refresh
    } catch (error) {
      toast.error("Failed to load leads");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Delete functions
  const handleDeleteLead = async (leadId) => {
    if (!window.confirm("Are you sure you want to delete this lead?")) return;
    
    setDeleting(true);
    try {
      await api.delete(`/leads/${leadId}`);
      toast.success("Lead deleted");
      fetchLeads();
    } catch (error) {
      toast.error("Failed to delete lead");
      console.error(error);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedLeadIds.length === 0) {
      toast.error("No leads selected");
      return;
    }
    
    if (!window.confirm(`Delete ${selectedLeadIds.length} selected leads?`)) return;
    
    setDeleting(true);
    try {
      await api.delete('/leads/bulk', {
        data: { lead_ids: selectedLeadIds }
      });
      toast.success(`Deleted ${selectedLeadIds.length} leads`);
      setSelectedLeadIds([]);
      fetchLeads();
    } catch (error) {
      toast.error("Failed to delete leads");
      console.error(error);
    } finally {
      setDeleting(false);
    }
  };

  const toggleSelectLead = (leadId) => {
    setSelectedLeadIds(prev => 
      prev.includes(leadId) 
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedLeadIds.length === filteredLeads.length) {
      setSelectedLeadIds([]);
    } else {
      setSelectedLeadIds(filteredLeads.map(l => l.id));
    }
  };

  const fetchVerificationStats = async () => {
    try {
      const response = await api.get('/leads/verification-stats');
      setVerificationStats(response.data);
    } catch (error) {
      console.error("Failed to fetch verification stats:", error);
    }
  };

  const handleVerifyBatch = async () => {
    setVerifying(true);
    try {
      const response = await api.post('/leads/verify-batch', { max_leads: 20 });
      toast.success(`Verified ${response.data.total_verified} leads: ${response.data.valid_count} valid, ${response.data.login_wall_count} login walls`);
      fetchLeads();
      fetchVerificationStats();
    } catch (error) {
      toast.error("Verification failed");
      console.error(error);
    } finally {
      setVerifying(false);
    }
  };

  useEffect(() => {
    fetchLeads();
    fetchVerificationStats();
  }, [statusFilter, sourceFilter]);

  const handleAddLead = async () => {
    try {
      const leadData = {
        ...newLead,
        tech_stack: newLead.tech_stack.split(",").map(s => s.trim()).filter(Boolean)
      };
      await api.post('/leads', leadData);
      toast.success("Lead added successfully");
      setIsAddDialogOpen(false);
      setNewLead({
        title: "",
        client: "",
        rate: "",
        location: "",
        clearance: "",
        tech_stack: "",
        link: "",
        source: "manual",
        notes: ""
      });
      fetchLeads();
    } catch (error) {
      toast.error("Failed to add lead");
      console.error(error);
    }
  };

  // Export leads to CSV
  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const response = await api.get('/leads/export/csv', {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `westbay_leads_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Leads exported successfully");
    } catch (error) {
      toast.error("Failed to export leads");
      console.error(error);
    } finally {
      setExporting(false);
    }
  };

  // Import leads from CSV
  const handleImportCSV = async () => {
    if (!importFile) {
      toast.error("Please select a CSV file");
      return;
    }
    
    setImporting(true);
    setImportResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      
      const response = await api.post('/leads/import/csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setImportResult(response.data);
      toast.success(`Imported ${response.data.imported} leads`);
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import leads");
      console.error(error);
    } finally {
      setImporting(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'text/csv') {
      setImportFile(file);
      setImportResult(null);
    } else if (file) {
      toast.error("Please select a valid CSV file");
    }
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-4 h-4 ml-1 opacity-50" />;
    return sortDirection === "asc" 
      ? <ArrowUp className="w-4 h-4 ml-1" /> 
      : <ArrowDown className="w-4 h-4 ml-1" />;
  };

  const filteredLeads = useMemo(() => {
    const searchLower = searchTerm.toLowerCase();
    
    // Filter first
    let result = leads.filter(lead => (
      lead.title?.toLowerCase().includes(searchLower) ||
      lead.client?.toLowerCase().includes(searchLower) ||
      lead.tech_stack?.some(tech => tech.toLowerCase().includes(searchLower))
    ));
    
    // Then sort
    result.sort((a, b) => {
      let aVal, bVal;
      
      switch (sortField) {
        case "score":
          aVal = a.score || 0;
          bVal = b.score || 0;
          break;
        case "title":
          aVal = a.title?.toLowerCase() || "";
          bVal = b.title?.toLowerCase() || "";
          break;
        case "client":
          aVal = a.client?.toLowerCase() || "";
          bVal = b.client?.toLowerCase() || "";
          break;
        case "rate":
          aVal = parseFloat(a.rate?.replace(/[^0-9.]/g, "")) || 0;
          bVal = parseFloat(b.rate?.replace(/[^0-9.]/g, "")) || 0;
          break;
        case "location":
          aVal = a.location?.toLowerCase() || "";
          bVal = b.location?.toLowerCase() || "";
          break;
        case "status":
          aVal = a.status?.toLowerCase() || "";
          bVal = b.status?.toLowerCase() || "";
          break;
        case "created_at":
          aVal = new Date(a.created_at || 0).getTime();
          bVal = new Date(b.created_at || 0).getTime();
          break;
        default:
          aVal = a.score || 0;
          bVal = b.score || 0;
      }
      
      if (typeof aVal === "string") {
        return sortDirection === "asc" 
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      
      return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
    });
    
    return result;
  }, [leads, searchTerm, sortField, sortDirection]);

  const getScoreColor = (score) => {
    if (score >= 80) return "text-emerald-400";
    if (score >= 50) return "text-theme-secondary";
    return "text-theme-danger";
  };

  const getLinkStatusIcon = (status) => {
    switch (status) {
      case "valid":
        return <ShieldCheck className="w-4 h-4 text-emerald-400" title="Link verified - valid job posting" />;
      case "login_wall":
        return <ShieldAlert className="w-4 h-4 text-theme-accent" title="Login required to view" />;
      case "invalid":
      case "timeout":
        return <ShieldX className="w-4 h-4 text-theme-danger" title="Invalid or expired link" />;
      case "unknown":
        return <ShieldQuestion className="w-4 h-4 text-theme-muted" title="Could not determine" />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6" data-testid="leads-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-14 h-12 rounded-xl bg-gradient-to-br from-[#f4a261] to-[#e76f51] flex items-center justify-center" style={{boxShadow: '4px 4px 0 rgba(139, 69, 19, 0.4)'}}>
            <FileText className="w-7 h-7 text-theme-dark" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-theme-light" style={{textShadow: '2px 2px 4px rgba(0,0,0,0.3)'}}>
              Leads
            </h1>
            <p className="text-theme-muted text-sm font-mono">
              MANAGE CONTRACT OPPORTUNITIES
            </p>
          </div>
        </div>
        <div className="flex gap-3 flex-wrap">
          {/* Export CSV Button */}
          <Button 
            className="bg-gradient-to-r from-[#264653] to-[#1a3a42] hover:from-[#2a9d8f] hover:to-[#264653] text-theme-light border-2 border-theme-primary/50 font-bold"
            style={{boxShadow: '3px 3px 0 rgba(42, 157, 143, 0.3)'}}
            onClick={handleExportCSV}
            disabled={exporting || leads.length === 0}
            data-testid="export-csv-btn"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            EXPORT
          </Button>

          {/* Import CSV Dialog */}
          <Dialog open={isImportDialogOpen} onOpenChange={(open) => {
            setIsImportDialogOpen(open);
            if (!open) {
              setImportFile(null);
              setImportResult(null);
            }
          }}>
            <DialogTrigger asChild>
              <Button 
                className="bg-gradient-to-r from-[#264653] to-[#1a3a42] hover:from-[#f4a261] hover:to-[#264653] text-theme-light border-2 border-theme-accent/50 font-bold"
                style={{boxShadow: '3px 3px 0 rgba(244, 162, 97, 0.3)'}}
                data-testid="import-csv-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                IMPORT
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-theme-darker border-2 border-theme-secondary/40 max-w-md" style={{boxShadow: '6px 6px 0 rgba(139, 69, 19, 0.3)'}}>
              <DialogHeader>
                <DialogTitle className="text-theme-light text-xl font-bold flex items-center gap-2">
                  <FileUp className="w-5 h-5 text-theme-secondary" />
                  Import Leads from CSV
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                {/* File Input */}
                <div 
                  className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
                    importFile ? 'border-theme-primary bg-theme-primary/10' : 'border-theme-primary/40 hover:border-theme-secondary/60'
                  }`}
                >
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    className="hidden"
                    id="csv-file-input"
                    data-testid="csv-file-input"
                  />
                  <label htmlFor="csv-file-input" className="cursor-pointer">
                    {importFile ? (
                      <div className="flex flex-col items-center gap-2">
                        <CheckCircle className="w-10 h-10 text-theme-primary" />
                        <p className="text-theme-light font-medium">{importFile.name}</p>
                        <p className="text-xs text-theme-muted">Click to change file</p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-2">
                        <Upload className="w-10 h-10 text-theme-muted" />
                        <p className="text-theme-light">Click to select CSV file</p>
                        <p className="text-xs text-theme-muted">or drag and drop</p>
                      </div>
                    )}
                  </label>
                </div>

                {/* CSV Format Info */}
                <div className="p-3 rounded-lg bg-theme-dark/50 border border-theme-primary/30">
                  <p className="text-xs text-theme-muted font-mono mb-2">REQUIRED COLUMNS:</p>
                  <p className="text-xs text-theme-light">title, client</p>
                  <p className="text-xs text-theme-muted font-mono mt-2">OPTIONAL COLUMNS:</p>
                  <p className="text-xs text-theme-light">rate, location, clearance, tech_stack, link, source, notes</p>
                </div>

                {/* Import Result */}
                {importResult && (
                  <div className={`p-3 rounded-lg border ${
                    importResult.errors?.length > 0 
                      ? 'bg-theme-danger/10 border-theme-danger/30' 
                      : 'bg-theme-primary/10 border-theme-primary/30'
                  }`}>
                    <div className="flex items-center gap-2 mb-2">
                      {importResult.errors?.length > 0 ? (
                        <AlertCircle className="w-4 h-4 text-theme-danger" />
                      ) : (
                        <CheckCircle className="w-4 h-4 text-theme-primary" />
                      )}
                      <p className="text-sm font-medium text-theme-light">
                        Imported {importResult.imported} of {importResult.total} leads
                      </p>
                    </div>
                    {importResult.errors?.length > 0 && (
                      <div className="mt-2 max-h-24 overflow-y-auto">
                        {importResult.errors.slice(0, 5).map((err, idx) => (
                          <p key={idx} className="text-xs text-theme-danger">{err}</p>
                        ))}
                        {importResult.errors.length > 5 && (
                          <p className="text-xs text-theme-muted">...and {importResult.errors.length - 5} more errors</p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Import Button */}
                <Button
                  onClick={handleImportCSV}
                  disabled={!importFile || importing}
                  className="w-full bg-gradient-to-r from-[#e9c46a] to-[#f4a261] hover:from-[#f4a261] hover:to-[#e76f51] text-theme-dark font-bold"
                  style={{boxShadow: '3px 3px 0 rgba(233, 196, 106, 0.4)'}}
                  data-testid="import-submit-btn"
                >
                  {importing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      IMPORTING...
                    </>
                  ) : (
                    <>
                      <FileUp className="w-4 h-4 mr-2" />
                      IMPORT LEADS
                    </>
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Verify Links Button */}
          <Button 
            className="bg-gradient-to-r from-[#e9c46a] to-[#f4a261] hover:from-[#f4a261] hover:to-[#e76f51] text-[#1a3a42] border-2 border-theme-secondary/50 font-bold"
            style={{boxShadow: '3px 3px 0 rgba(233, 196, 106, 0.4)'}}
            onClick={handleVerifyBatch}
            disabled={verifying}
            data-testid="verify-links-btn"
          >
            {verifying ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <ShieldCheck className="w-4 h-4 mr-2" />
            )}
            {verifying ? "VERIFYING..." : "VERIFY LINKS"}
          </Button>
          
          {/* Add Lead Dialog */}
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-gradient-to-r from-[#2a9d8f] to-[#1a6b62] hover:from-[#40c9ba] hover:to-[#2a9d8f] text-theme-light border-2 border-[#40c9ba]/50 font-bold"
                style={{boxShadow: '3px 3px 0 rgba(42, 157, 143, 0.4)'}}
                data-testid="add-lead-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                ADD LEAD
              </Button>
            </DialogTrigger>
          <DialogContent className="bg-theme-darker border-2 border-theme-secondary/40 max-w-lg" style={{boxShadow: '6px 6px 0 rgba(139, 69, 19, 0.3)'}}>
            <DialogHeader>
              <DialogTitle className="text-theme-light text-xl font-bold">Add New Lead</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-theme-muted font-mono text-xs">TITLE *</Label>
                  <Input
                    value={newLead.title}
                    onChange={(e) => setNewLead({ ...newLead, title: e.target.value })}
                    className="bg-theme-dark border-2 border-theme-primary/40 mt-1 text-theme-light focus:border-theme-secondary"
                    placeholder="Sr. Cloud Engineer"
                    data-testid="lead-title-input"
                  />
                </div>
                <div>
                  <Label className="text-theme-muted font-mono text-xs">CLIENT *</Label>
                  <Input
                    value={newLead.client}
                    onChange={(e) => setNewLead({ ...newLead, client: e.target.value })}
                    className="bg-theme-dark border-2 border-theme-primary/40 mt-1 text-theme-light focus:border-theme-secondary"
                    placeholder="Company Name"
                    data-testid="lead-client-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-theme-muted font-mono text-xs">RATE</Label>
                  <Input
                    value={newLead.rate}
                    onChange={(e) => setNewLead({ ...newLead, rate: e.target.value })}
                    className="bg-theme-dark border-2 border-theme-primary/40 mt-1 text-theme-light focus:border-theme-secondary"
                    placeholder="$150-180/hr"
                    data-testid="lead-rate-input"
                  />
                </div>
                <div>
                  <Label className="text-theme-muted font-mono text-xs">LOCATION *</Label>
                  <Input
                    value={newLead.location}
                    onChange={(e) => setNewLead({ ...newLead, location: e.target.value })}
                    className="bg-theme-dark border-2 border-theme-primary/40 mt-1 text-theme-light focus:border-theme-secondary"
                    placeholder="Remote / Washington DC"
                    data-testid="lead-location-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-theme-muted font-mono text-xs">CLEARANCE</Label>
                  <Input
                    value={newLead.clearance}
                    onChange={(e) => setNewLead({ ...newLead, clearance: e.target.value })}
                    className="bg-theme-dark border-2 border-theme-primary/40 mt-1 text-theme-light focus:border-theme-secondary"
                    placeholder="Secret / TS/SCI"
                    data-testid="lead-clearance-input"
                  />
                </div>
                <div>
                  <Label className="text-theme-muted font-mono text-xs">SOURCE</Label>
                  <Select
                    value={newLead.source}
                    onValueChange={(value) => setNewLead({ ...newLead, source: value })}
                  >
                    <SelectTrigger className="bg-theme-dark border-2 border-theme-primary/40 mt-1 text-theme-light" data-testid="lead-source-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-theme-dark border-2 border-theme-primary/40">
                      {SOURCES.map(source => (
                        <SelectItem key={source.value} value={source.value} className="text-theme-light focus:bg-theme-primary/30">
                          {source.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-theme-muted font-mono text-xs">TECH STACK (COMMA SEPARATED)</Label>
                <Input
                  value={newLead.tech_stack}
                  onChange={(e) => setNewLead({ ...newLead, tech_stack: e.target.value })}
                  className="bg-theme-dark border-2 border-theme-primary/40 mt-1 text-theme-light focus:border-theme-secondary"
                  placeholder="AWS, Terraform, Kubernetes, Python"
                  data-testid="lead-tech-input"
                />
              </div>
              <div>
                <Label className="text-theme-muted font-mono text-xs">LINK</Label>
                <Input
                  value={newLead.link}
                  onChange={(e) => setNewLead({ ...newLead, link: e.target.value })}
                  className="bg-theme-dark border-2 border-theme-primary/40 mt-1 text-theme-light focus:border-theme-secondary"
                  placeholder="https://..."
                  data-testid="lead-link-input"
                />
              </div>
              <div>
                <Label className="text-theme-muted font-mono text-xs">NOTES</Label>
                <Textarea
                  value={newLead.notes}
                  onChange={(e) => setNewLead({ ...newLead, notes: e.target.value })}
                  className="bg-theme-dark border-2 border-theme-primary/40 mt-1 text-theme-light focus:border-theme-secondary"
                  placeholder="Any additional notes..."
                  data-testid="lead-notes-input"
                />
              </div>
              <Button 
                onClick={handleAddLead}
                className="w-full bg-gradient-to-r from-[#e9c46a] to-[#d4a84b] hover:from-[#f4d888] hover:to-[#e9c46a] text-theme-dark font-bold border-2 border-[#f4d888]/50"
                style={{boxShadow: '3px 3px 0 rgba(139, 69, 19, 0.3)'}}
                disabled={!newLead.title || !newLead.client || !newLead.location}
                data-testid="submit-lead-btn"
              >
                ADD LEAD
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Verification Stats Bar */}
      {verificationStats && (
        <div className="p-3 rounded-xl flex items-center justify-between" style={{
          background: 'linear-gradient(145deg, rgba(26, 58, 66, 0.6), rgba(38, 70, 83, 0.5))',
          border: '2px solid rgba(233, 196, 106, 0.3)',
        }} data-testid="verification-stats-bar">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-emerald-400" />
              <span className="text-xs font-mono text-theme-muted">VALID: <span className="text-emerald-400">{verificationStats.verification_stats?.valid || 0}</span></span>
            </div>
            <div className="flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-theme-accent" />
              <span className="text-xs font-mono text-theme-muted">LOGIN WALL: <span className="text-theme-accent">{verificationStats.verification_stats?.login_wall || 0}</span></span>
            </div>
            <div className="flex items-center gap-2">
              <ShieldX className="w-4 h-4 text-theme-danger" />
              <span className="text-xs font-mono text-theme-muted">INVALID: <span className="text-theme-danger">{verificationStats.verification_stats?.invalid || 0}</span></span>
            </div>
            <div className="flex items-center gap-2">
              <ShieldQuestion className="w-4 h-4 text-theme-muted" />
              <span className="text-xs font-mono text-theme-muted">PENDING: <span className="text-theme-muted">{(verificationStats.verification_stats?.no_status || 0) + (verificationStats.verification_stats?.pending || 0)}</span></span>
            </div>
          </div>
          <span className="text-xs font-mono text-theme-secondary">
            {verificationStats.verified_percentage}% VERIFIED
          </span>
        </div>
      )}

      {/* Filters */}
      <div className="p-4 rounded-xl" style={{
        background: 'linear-gradient(145deg, rgba(26, 58, 66, 0.8), rgba(38, 70, 83, 0.7))',
        border: '2px solid rgba(42, 157, 143, 0.3)',
        boxShadow: '4px 4px 0 rgba(38, 70, 83, 0.3)'
      }}>
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-theme-primary" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-theme-dark border-2 border-theme-primary/40 text-theme-light placeholder:text-theme-muted/50"
                placeholder="Search leads..."
                data-testid="search-leads-input"
              />
            </div>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px] bg-theme-dark border-2 border-theme-primary/40 text-theme-light" data-testid="status-filter">
              <Filter className="w-4 h-4 mr-2 text-theme-secondary" />
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="bg-theme-dark border-2 border-theme-primary/40">
              <SelectItem value="all" className="text-theme-light">All Status</SelectItem>
              {STATUSES.map(status => (
                <SelectItem key={status.value} value={status.value} className="text-theme-light">
                  {status.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger className="w-[150px] bg-theme-dark border-2 border-theme-primary/40 text-theme-light" data-testid="source-filter">
              <Filter className="w-4 h-4 mr-2 text-theme-secondary" />
              <SelectValue placeholder="Source" />
            </SelectTrigger>
            <SelectContent className="bg-theme-dark border-2 border-theme-primary/40">
              <SelectItem value="all" className="text-theme-light">All Sources</SelectItem>
              {SOURCES.map(source => (
                <SelectItem key={source.value} value={source.value} className="text-theme-light">
                  {source.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button 
            variant="outline" 
            className="border-2 border-theme-primary/40 hover:bg-theme-primary/20 text-theme-primary-light"
            onClick={fetchLeads}
            data-testid="refresh-leads-btn"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Leads Table */}
      <div className="rounded-xl overflow-hidden" style={{
        background: 'linear-gradient(145deg, rgba(26, 58, 66, 0.9), rgba(38, 70, 83, 0.8))',
        border: '3px solid rgba(233, 196, 106, 0.3)',
        boxShadow: '6px 6px 0 rgba(139, 69, 19, 0.2)'
      }} data-testid="leads-table-card">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="w-8 h-8 animate-spin text-theme-primary" />
          </div>
        ) : filteredLeads.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-theme-muted">
            <Target className="w-16 h-16 mb-4 text-theme-primary/50" />
            <p className="font-mono">NO LEADS FOUND</p>
            <Button 
              variant="outline" 
              className="mt-4 border-2 border-theme-secondary/40 text-theme-secondary"
              onClick={() => setIsAddDialogOpen(true)}
            >
              <Plus className="w-4 h-4 mr-2" />
              ADD YOUR FIRST LEAD
            </Button>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-b-2 border-theme-primary/30 hover:bg-transparent">
                <TableHead className="w-10">
                  <input
                    type="checkbox"
                    checked={selectedLeadIds.length === filteredLeads.length && filteredLeads.length > 0}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 rounded accent-[#2a9d8f]"
                    data-testid="select-all-checkbox"
                  />
                </TableHead>
                <TableHead 
                  className="text-theme-secondary font-mono text-xs uppercase cursor-pointer hover:bg-theme-primary/10 transition-colors"
                  onClick={() => handleSort("score")}
                  data-testid="sort-score"
                >
                  <div className="flex items-center">
                    Score
                    <SortIcon field="score" />
                  </div>
                </TableHead>
                <TableHead 
                  className="text-theme-secondary font-mono text-xs uppercase cursor-pointer hover:bg-theme-primary/10 transition-colors"
                  onClick={() => handleSort("title")}
                  data-testid="sort-title"
                >
                  <div className="flex items-center">
                    Title
                    <SortIcon field="title" />
                  </div>
                </TableHead>
                <TableHead 
                  className="text-theme-secondary font-mono text-xs uppercase cursor-pointer hover:bg-theme-primary/10 transition-colors"
                  onClick={() => handleSort("client")}
                  data-testid="sort-client"
                >
                  <div className="flex items-center">
                    Client
                    <SortIcon field="client" />
                  </div>
                </TableHead>
                <TableHead 
                  className="text-theme-secondary font-mono text-xs uppercase cursor-pointer hover:bg-theme-primary/10 transition-colors"
                  onClick={() => handleSort("rate")}
                  data-testid="sort-rate"
                >
                  <div className="flex items-center">
                    Rate
                    <SortIcon field="rate" />
                  </div>
                </TableHead>
                <TableHead 
                  className="text-theme-secondary font-mono text-xs uppercase cursor-pointer hover:bg-theme-primary/10 transition-colors"
                  onClick={() => handleSort("location")}
                  data-testid="sort-location"
                >
                  <div className="flex items-center">
                    Location
                    <SortIcon field="location" />
                  </div>
                </TableHead>
                <TableHead 
                  className="text-theme-secondary font-mono text-xs uppercase cursor-pointer hover:bg-theme-primary/10 transition-colors"
                  onClick={() => handleSort("status")}
                  data-testid="sort-status"
                >
                  <div className="flex items-center">
                    Status
                    <SortIcon field="status" />
                  </div>
                </TableHead>
                <TableHead className="text-theme-secondary font-mono text-xs uppercase">Link</TableHead>
                <TableHead className="text-theme-secondary font-mono text-xs uppercase">Source</TableHead>
                <TableHead className="text-theme-secondary font-mono text-xs uppercase w-10">Del</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLeads.map((lead) => (
                <TableRow 
                  key={lead.id}
                  className={`border-b border-theme-primary/20 hover:bg-theme-primary/10 cursor-pointer transition-colors ${selectedLeadIds.includes(lead.id) ? 'bg-theme-primary/20' : ''}`}
                  onClick={() => setSelectedLead(lead)}
                  data-testid={`lead-row-${lead.id}`}
                >
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedLeadIds.includes(lead.id)}
                      onChange={() => toggleSelectLead(lead.id)}
                      className="w-4 h-4 rounded accent-[#2a9d8f]"
                    />
                  </TableCell>
                  <TableCell>
                    <span className={`font-mono font-bold text-lg ${getScoreColor(lead.score)}`} style={{textShadow: '0 0 10px currentColor'}}>
                      {lead.score}
                    </span>
                  </TableCell>
                  <TableCell className="font-medium text-theme-light max-w-[200px] truncate">
                    {lead.title}
                  </TableCell>
                  <TableCell className="text-theme-muted">{lead.client}</TableCell>
                  <TableCell className="font-mono text-theme-secondary">{lead.rate || "-"}</TableCell>
                  <TableCell className="text-theme-muted max-w-[150px] truncate">{lead.location}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`status-${lead.status} text-xs font-mono`}>
                      {lead.status.replace('_', ' ').toUpperCase()}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {getLinkStatusIcon(lead.link_status)}
                      {lead.link && (
                        <a 
                          href={lead.link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-theme-primary-light hover:text-theme-secondary"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`source-${lead.source} text-xs font-mono`}>
                      {lead.source.replace('_', ' ').toUpperCase()}
                    </Badge>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteLead(lead.id)}
                      disabled={deleting}
                      className="text-theme-danger hover:bg-theme-danger/20 p-1 h-8 w-8"
                      data-testid={`delete-lead-${lead.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        
        {/* Bulk Delete Button */}
        {selectedLeadIds.length > 0 && (
          <div className="fixed bottom-6 right-6 z-50">
            <Button
              onClick={handleDeleteSelected}
              disabled={deleting}
              className="bg-theme-danger hover:bg-theme-danger/80 text-white shadow-lg"
              data-testid="bulk-delete-btn"
            >
              {deleting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Delete {selectedLeadIds.length} Selected
            </Button>
          </div>
        )}
      </div>

      {/* Lead Detail Sheet */}
      <LeadDetailSheet 
        lead={selectedLead} 
        open={!!selectedLead} 
        onClose={() => setSelectedLead(null)}
        onUpdate={fetchLeads}
      />
    </div>
  );
}
